import React, {useCallback, useMemo, useRef} from 'react';
import JoditEditor from 'jodit-react';
import './style.css';
import useLocalization from '../../../Localization';

const normalizeEditorValue = (html) => {
  if (!html) return '';

  const text = html
    .replace(/<(.|\n)*?>/g, '')
    .replace(/&nbsp;/g, '')
    .trim();

  if (text.length === 0) return '';

  // Add crossorigin="anonymous" to img tags so server-uploaded images display correctly
  const withCrossOrigin = html.replace(/<img(\s[^>]*)?>/gi, (match, attrs) => {
    if (attrs && /crossorigin=/i.test(attrs)) return match;
    return '<img crossorigin="anonymous"' + (attrs || '') + '>';
  });
  return withCrossOrigin;
};

const CommonEditor = ({
  value,
  onChange,
  onContentChange,
  placeholder,
  disabled,
  Variables,
  customVariables,
  v2 = false,
  removeExtraField = false,
  text,
  placeholderValue,
}) => {
  const {ApplicationString} = useLocalization();
  const editor = useRef(null);
  const enterText = ApplicationString['textEditor.enter'];
  const hereText = ApplicationString['textEditor.here'];

  const handleBlur = useCallback(
    (newContent) => {
      if (onChange) {
        const cleanedValue = normalizeEditorValue(newContent);
        onChange(cleanedValue);
      }
    },
    [onChange],
  );

  const handleJoditChange = useCallback(
    (newContent) => {
      if (!onContentChange) {
        return;
      }
      const cleanedValue = normalizeEditorValue(newContent);
      const cleanedCurrent = normalizeEditorValue(value);
      if (cleanedValue !== cleanedCurrent) {
        onContentChange(cleanedValue);
      }
    },
    [onContentChange, value],
  );

  const config = useMemo(
    () => ({
      placeholder: `${enterText} ${placeholderValue} ${hereText}`,
      // No uploader/filebrowser so image dialog shows only the URL option (no Upload or Browse tabs)
      createAttributes: {
        img: {
          crossorigin: 'anonymous',
        },
        table: {
          style:
            'border-collapse: collapse; font-family: Arial, sans-serif; font-size: 14px;border:1px solid #C5C6C8FF;width: 800px;',
        },
        tr: {style: ' border: 1px solid #C5C6C8FF;'},
        td: {style: ' border: 1px solid #C5C6C8FF;'},
      },
      buttons: removeExtraField
        ? 'bold,italic,underline,fontsize,font,|,ul,ol,align,|,undo,redo,source,|,copy,paste,link,image,|'
        : 'bold,italic,underline,fontsize,font,brush,|,ul,ol,align,|,undo,redo,source,|,copy,paste,link,image,|',
      toolbarAdaptive: false,
      style: {
        fontSize: '14px',
        fontFamily: 'inter',
        backgroundColor: '#f8f8f8',
        color: '#333',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      },
      controls: {
        font: {
          list: {
            '': 'Default',
          },
        },
      },
      showTooltip: true,
      showTooltipDelay: 50,
      observer: {
        breakpoints: {
          mobile: 480,
          tablet: 768,
          desktop: 1024,
        },
      },
      extraButtons: Variables
        ? [
            {
              name: 'table',
              icon: 'table',
            },
            ,
            {
              name: 'Dynamic Variables',
              icon: ``,
              list: Variables,
              exec: function (editor, t, {control}) {
                editor.selection.insertHTML(control.name);
              },
            },
          ]
        : {
            name: 'table',
            icon: 'table',
          },

      customVariables,
      clipboard: {
        pastePlain: true,
      },
    }),
    [
      enterText,
      hereText,
      placeholderValue,
      removeExtraField,
      Variables,
      customVariables,
    ],
  );

  return (
    <div
      className={`${v2 ? 'custom-class' : ''} errorBorderOnEditor`}
      id={`${text}-commonEditor-joditEditor`}>
      {placeholder && <div className='title-text'>{placeholder}</div>}
      <JoditEditor
        config={config}
        ref={editor}
        value={value}
        onBlur={handleBlur}
        onChange={handleJoditChange}
      />
    </div>
  );
};

export default CommonEditor;
